import React, { useState } from "react";
import Slider from "react-slick";
import { ChevronLeft, ChevronRight, Star, Quote } from "lucide-react";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

import img1 from "../assests/testimonalImg/Aman.png"; 
import img2 from "../assests/testimonalImg/Kanti.jpeg"; 
import img3 from "../assests/testimonalImg/Ramesh.webp"; 
import img4 from "../assests/testimonalImg/Ritu.jpg"; 
import img5 from "../assests/testimonalImg/Swati.jpeg"; 

interface Testimonial {
  name: string;
  category: string;
  photoUrl: string;
  feedback: string;
  rating: number;
}

const testimonials: Testimonial[] = [
    { name: "Aman Rajput", category: "Ring Purchase", photoUrl: img1, rating: 5, 
      feedback: "The quality of the ring is exceptional, and the service was outstanding. I highly recommend Sardar Jewellers for their unique and timeless designs.", },
    { name: "Kalyani Singh", category: "Gold Chain", photoUrl: img2, rating: 4, 
      feedback: "My gold chain arrived quickly and was exactly as described. The online experience was smooth, and the craftsmanship is truly beautiful.", },
    { name: "Ramesh Jha", category: "Diamond Necklace", photoUrl: img3, rating: 5, 
      feedback: "I am absolutely thrilled with my diamond necklace. It sparkles wonderfully and has become my favorite piece of jewelry. Truly worth the investment!", },
    { name: "Ritu Sharma", category: "Gold Bangles", photoUrl: img4, rating: 5, 
      feedback: "The gold bangles for the wedding were delivered perfectly. They look even more stunning in person than they did online. A highly reliable service.", },
    { name: "Swati Devi", category: "Wedding Jewelry", photoUrl: img5, rating: 4, 
      feedback: "Sardar Jewellers provided the perfect wedding collection. Excellent variety and professional guidance made the whole process stress-free.", },
];

const RatingStars: React.FC<{ count: number }> = ({ count }) => {
    return (
        <div className="flex justify-start">
            {[...Array(5)].map((_, i) => (
                <Star
                    key={i}
                    className={`w-3 h-3 sm:w-4 sm:h-4 ${i < count ? 'text-amber-500 fill-amber-500' : 'text-gray-300'}`} 
                />
            ))}
        </div>
    );
};

// Fixed Arrows: Hidden on mobile to prevent overflow, moved inside on Tablet
const NextArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button 
            className="absolute -right-2 md:-right-10 top-1/2 -translate-y-1/2 z-20 cursor-pointer p-2 md:p-3 rounded-full bg-white/90 shadow-md hover:bg-amber-50 transition hidden sm:flex border border-gray-100" 
            onClick={onClick}
            aria-label="Next Slide"
        >
            <ChevronRight className="w-5 h-5 text-amber-700" />
        </button>
    );
};

const PrevArrow = (props: any) => {
    const { onClick } = props;
    return (
        <button 
            className="absolute -left-2 md:-left-10 top-1/2 -translate-y-1/2 z-20 cursor-pointer p-2 md:p-3 rounded-full bg-white/90 shadow-md hover:bg-amber-50 transition hidden sm:flex border border-gray-100" 
            onClick={onClick}
            aria-label="Previous Slide"
        >
            <ChevronLeft className="w-5 h-5 text-amber-700" />
        </button>
    );
};

const TestimonialPage = () => {
  const [activeSlide, setActiveSlide] = useState(0); 

  const settings = {
    dots: true, // Dots are useful on mobile for orientation
    infinite: true,
    speed: 600,
    autoplay: true,
    autoplaySpeed: 5000,
    slidesToShow: 3,
    slidesToScroll: 1,
    swipeToSlide: true,
    touchThreshold: 20,
    nextArrow: <NextArrow />,
    prevArrow: <PrevArrow />,
    centerMode: true, 
    centerPadding: '0px', 
    beforeChange: (current: number, next: number) => setActiveSlide(next), 
    
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          centerPadding: '15%', // Shows side slides partially
        }
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          centerPadding: '30px', // Tightened for mobile
          arrows: false, // Force touch usage on small screens
        }
      }
    ]
  };

  return (
    <section className="bg-white py-12 md:py-20 px-4 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-10 md:mb-16">
            <h2 className="text-2xl md:text-4xl font-extrabold text-amber-800 font-[Cinzel] inline-block relative pb-4">
                Our Valued Customers
                <span className="absolute left-1/2 bottom-0 -translate-x-1/2 h-1 w-16 md:w-20 bg-amber-500"></span>
            </h2>
        </div>
        
        <div className="relative md:px-12">
          <Slider {...settings}>
            {testimonials.map((testimonial, index) => {
              const isActive = index === activeSlide; 

              return (
                <div 
                  key={index} 
                  className="px-2 md:px-4 py-6"
                >
                  <div 
                    className={`p-6 md:p-8 min-h-[320px] bg-white rounded-xl transition-all duration-500 flex flex-col justify-between border ${
                        isActive 
                        ? 'shadow-2xl border-amber-200 scale-100 md:scale-110 opacity-100' 
                        : 'shadow-sm border-gray-100 scale-90 opacity-40 md:opacity-60 grayscale-[0.5]'
                    }`}
                  >
                    <div className="text-amber-500 mb-4">
                       <Quote className="w-8 h-8 fill-amber-500 opacity-20" />
                    </div>
                    
                    <p className="text-gray-700 text-sm md:text-base italic leading-relaxed mb-6 flex-grow">
                      "{testimonial.feedback}"
                    </p>
                    
                    <div className="pt-4 border-t border-gray-100 flex items-center">
                      <img
                        src={testimonial.photoUrl}
                        alt={testimonial.name}
                        className="w-10 h-10 md:w-12 md:h-12 rounded-full object-cover border-2 border-amber-400 mr-3 flex-shrink-0"
                      />
                      <div className="overflow-hidden">
                        <h3 className="text-sm md:text-md font-bold text-gray-800 truncate">
                          {testimonial.name}
                        </h3>
                        <p className="text-[10px] md:text-xs text-amber-600 font-medium mb-1">{testimonial.category}</p>
                        <RatingStars count={testimonial.rating} />
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </Slider>
        </div>
      </div>

      {/* Custom Styles for Pagination Dots */}
      <style>{`
        .slick-dots li button:before {
            color: #d97706 !important;
        }
        .slick-dots {
            bottom: -30px;
        }
      `}</style>
    </section>
  );
};

export default TestimonialPage;